import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking"); // checking | success | failed
  const [product, setProduct] = useState("premium_guide");
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!loading && user && !sessionId) {
      navigate("/quickstart", { replace: true });
    }
  }, [loading, user, sessionId, navigate]);

  useEffect(() => {
    if (!sessionId || !user) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
        if (data.product) setProduct(data.product);
        if (data.payment_status === "paid") {
          setStatus("success");
          return;
        }
        if (data.status === "expired") {
          setStatus("failed");
          return;
        }
        attempts++;
        if (attempts < 6) setTimeout(poll, 2000);
        else setStatus("failed");
      } catch {
        attempts++;
        if (attempts < 6) setTimeout(poll, 2000);
        else setStatus("failed");
      }
    };
    poll();
  }, [sessionId, user]);

  if (loading || !user) return null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--c-bg)] px-4">
      <div className="w-full max-w-md text-center">
        {status === "checking" && (
          <div data-testid="payment-checking" className="rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] p-10 shadow-[6px_6px_0px_var(--c-shadow)]">
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-[#1E3A8A]" />
            <h2 className="mt-4 font-heading text-2xl font-bold text-[var(--c-text)]">Processing Payment...</h2>
            <p className="mt-2 text-[var(--c-muted)]">Please wait while we confirm your payment.</p>
          </div>
        )}
        {status === "success" && (
          <div data-testid="payment-success" className="rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] p-10 shadow-[6px_6px_0px_var(--c-shadow)]">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border-2 border-[#A3E6D5] bg-[#A3E6D5]/20">
              <CheckCircle2 className="h-8 w-8 text-[#1A1918]" />
            </div>
            <h2 className="mt-4 font-heading text-2xl font-bold text-[var(--c-text)]">Payment Successful!</h2>
            {product === "premium_guide" && (
              <p className="mt-2 text-[var(--c-muted)]">You now have full access to all 9 steps + the Cost Estimator. Let&apos;s continue building your LLC.</p>
            )}
            {product === "registered_agent" && (
              <p className="mt-2 text-[var(--c-muted)]">Thank you for purchasing the Registered Agent Service. We&apos;ll email you within 1 business day to confirm your address and start your 12-month coverage.</p>
            )}
            {product === "operating_agreement" && (
              <p className="mt-2 text-[var(--c-muted)]">Thank you for ordering the Operating Agreement. We&apos;ll email you within 1 business day with a short intake form, and deliver your draft within 3 business days.</p>
            )}
            <button
              data-testid="continue-to-ebook"
              onClick={() => navigate("/ebook")}
              className="mt-6 inline-flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] px-6 py-3 font-bold text-white shadow-[4px_4px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5"
            >
              {product === "premium_guide" ? "Continue Reading" : "Back to Guide"}
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        )}
        {status === "failed" && (
          <div data-testid="payment-failed" className="rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] p-10 shadow-[6px_6px_0px_var(--c-shadow)]">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border-2 border-[#FF4D4D] bg-[#FF4D4D]/10">
              <XCircle className="h-8 w-8 text-[#FF4D4D]" />
            </div>
            <h2 className="mt-4 font-heading text-2xl font-bold text-[var(--c-text)]">Payment Issue</h2>
            <p className="mt-2 text-[var(--c-muted)]">We couldn&apos;t confirm your payment. Please try again or contact support.</p>
            <button
              data-testid="back-to-ebook"
              onClick={() => navigate("/ebook")}
              className="mt-6 inline-flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-6 py-3 font-bold text-[var(--c-text)] shadow-[4px_4px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5"
            >
              Back to Guide
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
